import React, { useState, useEffect } from 'react';

const MASTIVIBESHomepage = () => {
  const [animatedText, setAnimatedText] = useState('');
  const [currentUser, setCurrentUser] = useState(0);
  const fullText = "Connect, Create & Celebrate - Your Ultimate Social Hub";

  const userTypes = [
    { 
      type: "Content Creators", 
      description: "Create stunning content and build your community",
      icon: "🎬",
      color: "from-pink-500 to-purple-500"
    },
    { 
      type: "Social Groups", 
      description: "Create groups, host events and connect with like-minded people",
      icon: "👥",
      color: "from-orange-500 to-yellow-500"
    },
    { 
      type: "Video Callers", 
      description: "HD video calls with up to 100 participants",
      icon: "📹",
      color: "from-cyan-500 to-blue-500"
    },
    { 
      type: "Event Hosts", 
      description: "Host virtual and real-world events with ease",
      icon: "🎉",
      color: "from-green-500 to-teal-500"
    }
  ];

  // Social media and communication icons for floating animation
  const socialIcons = [
    { icon: '🎥', size: 'w-12 h-12', position: 'top-10 left-10', delay: 'delay-0' },
    { icon: '📞', size: 'w-10 h-10', position: 'top-20 right-20', delay: 'delay-1000' },
    { icon: '👥', size: 'w-11 h-11', position: 'bottom-20 left-20', delay: 'delay-2000' },
    { icon: '🎭', size: 'w-9 h-9', position: 'bottom-10 right-10', delay: 'delay-1500' },
    { icon: '🎵', size: 'w-10 h-10', position: 'top-1/3 left-1/4', delay: 'delay-500' },
    { icon: '💬', size: 'w-8 h-8', position: 'bottom-1/3 right-1/4', delay: 'delay-2500' },
    { icon: '✨', size: 'w-11 h-11', position: 'top-1/4 right-1/3', delay: 'delay-3000' },
    { icon: '🎊', size: 'w-9 h-9', position: 'bottom-1/4 left-1/3', delay: 'delay-3500' }
  ];

  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setAnimatedText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 30);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const userInterval = setInterval(() => {
      setCurrentUser((prev) => (prev + 1) % userTypes.length);
    }, 3000);

    return () => clearInterval(userInterval);
  }, []);

  return (
    <div id="home" className="min-h-screen bg-gradient-to-br from-fuchsia-700 via-pink-600 to-orange-500 pt-20 overflow-hidden relative">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-yellow-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl animate-pulse delay-500"></div>
        
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div 
            key={i}
            className="absolute w-2 h-2 bg-white rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.2}s`,
              opacity: 0.7
            }}
          />
        ))}
      </div>

      {/* Floating Social Icons */}
      {socialIcons.map((icon, index) => (
        <div
          key={index}
          className={`absolute ${icon.position} ${icon.size} bg-white/20 backdrop-blur-lg rounded-2xl border-2 border-white/30 flex items-center justify-center text-2xl animate-float-slow ${icon.delay} z-20 shadow-2xl hover:scale-125 transition-transform duration-300 cursor-pointer`}
          style={{
            animationDuration: `${6 + index}s`
          }}
        >
          {icon.icon}
        </div>
      ))}

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Side - Content */}
          <div className="space-y-8">
            {/* Animated Main Heading */}
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-md rounded-full px-5 py-2 border-2 border-white/30 shadow-lg">
                <span className="w-3 h-3 bg-yellow-400 rounded-full animate-ping shadow-lg"></span>
                <span className="text-sm text-white font-bold">Join 500,000+ Active Users Worldwide</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight">
                <span className="bg-gradient-to-r from-yellow-300 via-pink-400 to-orange-400 bg-clip-text text-transparent animate-gradient-flow">
                  {animatedText}
                </span>
                <span className="animate-pulse text-yellow-300">|</span>
              </h1>
              
              <p className="text-xl text-white/90 leading-relaxed font-medium">
                MASTIVIBES is the ultimate social platform where you can create custom fields, host group video calls, 
                build communities, and connect with people who share your passions. Your space, your rules!
              </p>

              {/* Animated User Type */}
              <div className="flex items-center space-x-4 p-6 bg-white/10 backdrop-blur-xl rounded-2xl border-2 border-white/20 hover:bg-white/15 transition-all duration-300 transform hover:scale-[1.02] shadow-lg">
                <div className={`text-5xl transform transition-all duration-500 ${userTypes[currentUser].color} animate-bounce`}>
                  {userTypes[currentUser].icon}
                </div>
                <div className="flex-1">
                  <div className="text-white font-bold text-xl flex items-center">
                    <span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                      Perfect for {userTypes[currentUser].type}
                    </span>
                    <span className="ml-2 text-xs px-2 py-1 bg-yellow-500/30 rounded-full">NEW</span>
                  </div>
                  <div className="text-white/80 mt-1">
                    {userTypes[currentUser].description}
                  </div>
                </div>
              </div>
            </div>

            {/* Features Grid - MASTIVIBES Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: '📹', text: 'Group Video Calls', sub: 'Up to 100 participants', color: 'from-cyan-500 to-blue-500' },
                { icon: '👥', text: 'Create Groups', sub: 'Public & Private communities', color: 'from-pink-500 to-purple-500' },
                { icon: '🎨', text: 'Custom Fields', sub: 'Create unique profiles', color: 'from-yellow-500 to-orange-500' },
                { icon: '🎉', text: 'Virtual Events', sub: 'Host & Join events', color: 'from-green-500 to-teal-500' }
              ].map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center space-x-4 p-4 bg-white/10 backdrop-blur-lg rounded-xl border-2 border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105 group transform hover:-translate-y-1"
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    {feature.icon}
                  </div>
                  <div>
                    <div className="text-white font-bold text-lg">{feature.text}</div>
                    <div className="text-white/70 text-sm">{feature.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-pink-500 to-orange-500 rounded-xl blur opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                <div className="relative bg-white/20 backdrop-blur-lg text-white font-bold py-4 px-10 rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center transform hover:-translate-y-1 border-2 border-white/30 shadow-2xl">
                  <span className="mr-3 text-2xl">🚀</span>
                  Join MASTIVIBES Now - FREE!
                  <span className="ml-3 text-xl group-hover:translate-x-2 transition-transform">→</span>
                </div>
              </button>
              <button className="border-3 border-yellow-400/50 text-yellow-300 hover:bg-yellow-400/20 font-bold py-4 px-8 rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center backdrop-blur-lg shadow-lg">
                <span className="mr-3 text-xl">📱</span>
                Download Mobile App
                <span className="ml-2 text-xs px-2 py-1 bg-yellow-500/30 rounded-full">BETA</span>
              </button>
            </div>
          </div>

          {/* Right Side - Interactive Feature Cards */}
          <div className="relative">
            {/* Main Interactive Feature Board */}
            <div className="bg-white/15 backdrop-blur-2xl rounded-3xl p-8 border-2 border-white/25 shadow-2xl transform hover:scale-[1.02] transition-all duration-500">
              <h3 className="text-2xl font-bold text-white mb-6 text-center bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                Platform Features
              </h3>
              
              <div className="grid grid-cols-2 gap-6">
                {[
                  { 
                    icon: '👥', 
                    title: 'Create Groups', 
                    desc: 'Build communities', 
                    color: 'from-pink-500 to-purple-500', 
                    features: ['Group Calls', 'Shared Media', 'Admin Controls'] 
                  },
                  { 
                    icon: '📹', 
                    title: 'Video Calls', 
                    desc: 'HD quality', 
                    color: 'from-cyan-500 to-blue-500', 
                    features: ['100 Participants', 'Screen Share', 'Filters'] 
                  },
                  { 
                    icon: '🎨', 
                    title: 'Custom Fields', 
                    desc: 'Personalize profiles', 
                    color: 'from-yellow-500 to-orange-500', 
                    features: ['Custom Forms', 'Tags', 'Categories'] 
                  },
                  { 
                    icon: '💬', 
                    title: 'Group Chat', 
                    desc: 'Real-time messaging', 
                    color: 'from-green-500 to-teal-500', 
                    features: ['File Share', 'Reactions', 'Polls'] 
                  },
                  { 
                    icon: '🎵', 
                    title: 'Voice Rooms', 
                    desc: 'Audio hangouts', 
                    color: 'from-purple-500 to-pink-500', 
                    features: ['Music Share', 'Voice Effects', 'Rooms'] 
                  },
                  { 
                    icon: '📱', 
                    title: 'Cross-Platform', 
                    desc: 'Any device', 
                    color: 'from-orange-500 to-red-500', 
                    features: ['Mobile', 'Desktop', 'Web'] 
                  }
                ].map((item, index) => (
                  <div 
                    key={index}
                    className="text-center group cursor-pointer transform hover:-translate-y-2 transition-all duration-300"
                  >
                    <div className={`relative w-20 h-20 mx-auto bg-gradient-to-r ${item.color} rounded-2xl flex items-center justify-center text-3xl text-white shadow-lg group-hover:scale-110 transition-transform duration-300 mb-3`}>
                      {item.icon}
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
                    </div>
                    <p className="text-white font-bold group-hover:text-yellow-300 transition-colors duration-300">
                      {item.title}
                    </p>
                    <p className="text-white/70 text-sm mt-1">
                      {item.desc}
                    </p>
                    <div className="mt-2 space-y-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {item.features.map((feature, fIndex) => (
                        <div key={fIndex} className="text-xs text-white/60">
                          • {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Floating Decorations */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-yellow-500/30 rounded-3xl blur-xl animate-bounce shadow-2xl"></div>
            <div className="absolute -bottom-6 -left-6 w-36 h-36 bg-pink-500/30 rounded-3xl blur-xl animate-bounce delay-1000 shadow-2xl"></div>
            
            {/* Animated Rings */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 border-3 border-yellow-400/30 rounded-full animate-spin-slow"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 border-2 border-pink-400/20 rounded-full animate-spin-slow-reverse"></div>
          </div>
        </div>

        {/* Platform Stats Section */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-20">
          {[
            { 
              number: '500K+', 
              label: 'Active Users', 
              icon: '👥', 
              gradient: 'from-yellow-400 to-orange-500',
              description: 'Growing daily'
            },
            { 
              number: '50K+', 
              label: 'Active Groups', 
              icon: '💬', 
              gradient: 'from-pink-500 to-purple-500',
              description: 'Communities created'
            },
            { 
              number: '10K+', 
              label: 'Daily Calls', 
              icon: '📹', 
              gradient: 'from-cyan-500 to-blue-500',
              description: 'Video conferences'
            },
            { 
              number: '24/7', 
              label: 'Live Support', 
              icon: '⚡', 
              gradient: 'from-green-500 to-teal-500',
              description: 'Always available'
            }
          ].map((stat, index) => (
            <div 
              key={index}
              className="text-center p-8 bg-white/10 backdrop-blur-xl rounded-2xl border-2 border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-105 transform hover:-translate-y-2 group shadow-xl"
            >
              <div className={`w-20 h-20 mx-auto mb-4 bg-gradient-to-r ${stat.gradient} rounded-2xl flex items-center justify-center text-3xl text-white group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-2xl`}>
                {stat.icon}
              </div>
              <div className="text-4xl font-black bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                {stat.number}
              </div>
              <div className="text-white font-bold text-lg mt-2">
                {stat.label}
              </div>
              <div className="text-white/60 text-sm mt-1">
                {stat.description}
              </div>
              <div className="mt-3 h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          ))}
        </div>

        {/* Quick Action Cards */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              title: 'Create Your Space',
              description: 'Design custom profiles with unique fields and categories',
              icon: '🏗️',
              action: 'Start Creating →',
              color: 'border-yellow-400/50 hover:bg-yellow-400/10'
            },
            {
              title: 'Host Group Calls',
              description: 'Video calls with friends, family, or business teams',
              icon: '🎥',
              action: 'Start Call →',
              color: 'border-pink-400/50 hover:bg-pink-400/10'
            },
            {
              title: 'Join Communities',
              description: 'Discover and join groups based on your interests',
              icon: '👥',
              action: 'Explore →',
              color: 'border-cyan-400/50 hover:bg-cyan-400/10'
            }
          ].map((action, index) => (
            <div 
              key={index}
              className={`p-6 rounded-2xl border-2 ${action.color} bg-white/5 backdrop-blur-lg transition-all duration-300 hover:scale-[1.03] group cursor-pointer shadow-lg`}
            >
              <div className="flex items-start space-x-4">
                <div className="text-4xl group-hover:scale-110 transition-transform duration-300">
                  {action.icon}
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-white mb-2">{action.title}</h4>
                  <p className="text-white/70 mb-4">{action.description}</p>
                  <button className="text-yellow-300 font-semibold flex items-center group-hover:underline">
                    {action.action}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spin-slow-reverse {
          from { transform: rotate(0deg); }
          to { transform: rotate(-360deg); }
        }
        @keyframes float-slow {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg); 
          }
          33% { 
            transform: translateY(-20px) rotate(5deg) scale(1.1); 
          }
          66% { 
            transform: translateY(10px) rotate(-3deg) scale(0.95); 
          }
        }
        @keyframes gradient-flow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-spin-slow {
          animation: spin-slow 15s linear infinite;
        }
        .animate-spin-slow-reverse {
          animation: spin-slow-reverse 20s linear infinite;
        }
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        .animate-gradient-flow {
          background-size: 200% auto;
          animation: gradient-flow 3s ease infinite;
        }
      `}</style>
    </div>
  );
};

export default MASTIVIBESHomepage;